import { c as _c } from "react/compiler-runtime";
import { useId } from 'react';
import { useMediaQuery } from '@shape-construction/hooks/src/useMediaQuery';
import { cn } from '../utils/classes';
import './BrandSpinner.css';
const sizeClassesMap = {
  xs: 'size-6',
  sm: 'size-7',
  md: 'size-8',
  lg: 'size-9',
  xl: 'size-10'
};

// Theme color primitives, so the gradient follows theme overrides. They are
// resolved at paint time; stopColor goes through style because presentation
// attributes cannot carry var().
const gradientStops = [{
  offset: '0',
  token: '--color-cyan-400'
}, {
  offset: '0.22',
  token: '--color-blue-400'
}, {
  offset: '0.45',
  token: '--color-indigo-500'
}, {
  offset: '0.65',
  token: '--color-purple-500'
}, {
  offset: '0.84',
  token: '--color-fuchsia-500'
}, {
  offset: '1',
  token: '--color-pink-400'
}];

// The full Shape logomark as a single evenodd compound path: the outer shield
// plus the inner triangle cut-out.
const LOGOMARK_PATH = 'M30.0672 5.61791C24.9796 8.59148 20.8216 15.7933 12.5057 30.197C4.1897 44.6006 0.0317234 51.8025 0.000317143 57.6952C-0.0424602 65.7215 4.24495 73.1475 11.2173 77.1236C16.3363 80.0428 24.6523 80.0428 41.2842 80.0428C57.9161 80.0428 66.2321 80.0428 71.351 77.1236C78.3234 73.1475 82.6108 65.7215 82.568 57.6952C82.5366 51.8025 78.3787 44.6006 70.0627 30.197C61.7467 15.7933 57.5887 8.59148 52.5012 5.61791C45.5716 1.56771 36.9968 1.56771 30.0672 5.61791ZM41.4516 23.4887C37.2299 23.4887 34.4154 28.3635 28.7865 38.1131L27.7304 39.9424C22.1014 49.692 19.2869 54.5668 21.3978 58.2229C23.5087 61.879 29.1376 61.879 40.3955 61.879H42.5077C53.7656 61.879 59.3945 61.879 61.5054 58.2229C63.6162 54.5668 60.8017 49.692 55.1728 39.9424L54.1167 38.1132C48.4878 28.3635 45.6733 23.4887 41.4516 23.4887Z';

/**
 * The branded loading indicator: the Shape logomark drawn as a thin outline
 * with a six-stop gradient flowing around the contour, optionally spinning
 * with a pulsed per-revolution easing.
 *
 * Reach for it wherever loading is a branded surface — route-level and
 * full-screen loading states, inline content panes, and thumbnail
 * placeholders (`FileThumbnail`'s default indicator). Prefer the generic
 * `Spinner` ring only for small utilitarian affordances such as button
 * loading states; remaining `Spinner` call sites are expected to converge
 * here over time.
 *
 * Under `prefers-reduced-motion` the spin and the gradient rotation are
 * disabled and the outline renders in the solid brand color.
 */
export const BrandSpinner = t0 => {
  const $ = _c(25);
  let className;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      size: t1,
      spin: t2,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
  }
  const size = t1 === undefined ? "md" : t1;
  const spin = t2 === undefined ? true : t2;
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)", false);
  const t3 = useId();
  let t4;
  if ($[5] !== t3) {
    let t5;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = /[^a-zA-Z0-9-]/g;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    t4 = t3.replaceAll(t5, "");
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const gradientId = `brand-spinner-gradient-${t4}`;
  const t5 = sizeClassesMap[size];
  let t6;
  if ($[8] !== className || $[9] !== spin || $[10] !== t5) {
    t6 = cn("brand-spinner", t5, {
      "brand-spinner-spin": spin
    }, className);
    $[8] = className;
    $[9] = spin;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = gradientStops.map(_temp);
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== prefersReducedMotion) {
    t8 = !prefersReducedMotion && <animateTransform attributeName="gradientTransform" type="rotate" from="0 41.5 41.5" to="360 41.5 41.5" dur="3s" repeatCount="indefinite" />;
    $[13] = prefersReducedMotion;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== gradientId || $[16] !== t8) {
    t9 = <defs><linearGradient id={gradientId} gradientUnits="userSpaceOnUse" x1="-18" y1="41.5" x2="101" y2="41.5">{t7}{t8}</linearGradient></defs>;
    $[15] = gradientId;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const t10 = `url(#${gradientId})`;
  let t11;
  if ($[18] !== t10) {
    t11 = <path className="brand-spinner-outline" d={LOGOMARK_PATH} fillRule="evenodd" clipRule="evenodd" fill="none" stroke={t10} vectorEffect="non-scaling-stroke" />;
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== props || $[21] !== t11 || $[22] !== t6 || $[23] !== t9) {
    t12 = <svg viewBox="-2 -2 87 87" aria-hidden="true" className={t6} {...props}>{t9}{t11}</svg>;
    $[20] = props;
    $[21] = t11;
    $[22] = t6;
    $[23] = t9;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  return t12;
};
function _temp(t0) {
  const {
    offset,
    token
  } = t0;
  return <stop key={token} offset={offset} style={{
    stopColor: `var(${token})`
  }} />;
}